import cn from 'classnames'
import { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { toggleSelectingItem } from 'utils/common'
import PreviewText from 'views/find-art/components/Quiz/PreviewText'
import { IndividualFindArtFormData } from 'views/find-art/types'

type ColorType = {
  value: string
  label: string
  colorClasses: string[]
}

export const COLOR_TYPES: ColorType[] = [
  {
    label: 'Blue',
    value: '#C5EDFD', //rgb color: rgb(197, 237, 253)
    colorClasses: ['bg-[#80ABF9]', 'bg-[#A3CCFB]', 'bg-[#C5EDFD]', 'bg-[#5D8AF7]', 'bg-[#3A6AF6]'],
  },
  {
    label: 'Red',
    value: '#ED5F59',
    colorClasses: ['bg-[#F5BDBC]', 'bg-[#EF827E]', 'bg-[#ED5F59]', 'bg-[#EB3222]', 'bg-[#9A1C13]'],
  },
  {
    label: 'Green',
    value: '#66C73B',
    colorClasses: ['bg-[#B8F8AE]', 'bg-[#7BE162]', 'bg-[#66C73B]', 'bg-[#438F29]', 'bg-[#296019]'],
  },
  {
    label: 'Yellow',
    value: '#FFFCB8',
    colorClasses: ['bg-[#FFFEE3]', 'bg-[#FFFECE]', 'bg-[#FFFCB8]', 'bg-[#FFFC9C]', 'bg-[#FEF986]'],
  },
  {
    label: 'Brown',
    value: '#B19783',
    colorClasses: ['bg-[#E4D6C9]', 'bg-[#CBB7A6]', 'bg-[#B19783]', 'bg-[#9E8370]', 'bg-[#8A6F5B]'],
  },
  {
    label: 'Orange',
    value: '#F2A066',
    colorClasses: ['bg-[#F7CBA3]', 'bg-[#F3A87B]', 'bg-[#F2A066]', 'bg-[#F0843F]', 'bg-[#ED632F]'],
  },
  {
    label: 'Purple',
    value: '#43179E',
    colorClasses: ['bg-[#9B2FF6]', 'bg-[#7721BD]', 'bg-[#43179E]', 'bg-[#2D0D76]', 'bg-[#2C0747]'],
  },
  {
    label: 'Pink',
    value: '#EF80BA',
    colorClasses: ['bg-[#F7CDE4]', 'bg-[#F4B2D5]', 'bg-[#EF80BA]', 'bg-[#EC5AA3]', 'bg-[#EB3581]'],
  },
  {
    label: 'Black',
    value: '#1E1E1E',
    colorClasses: ['bg-[#4D4D4D]', 'bg-[#3D3D3D]', 'bg-[#1E1E1E]', 'bg-[#111111]', 'bg-[#000000]'],
  },
  {
    label: 'White',
    value: '#FDFDFF',
    colorClasses: ['bg-[#FDFBFB]', 'bg-[#FCFDFB]', 'bg-[#FDFDFF]', 'bg-[#FDF9F9]', 'bg-[#FDFBFB]'],
  },
]

const ColorStep = ({ onNext }: { onNext: () => void }) => {
  const { watch, setValue, trigger, getValues } = useFormContext<IndividualFindArtFormData>()
  const selectedColors = watch('artColors') || []

  const makeHandleToggleArtStyle = useCallback(
    (colorType: ColorType) => () => {
      const selectedColors = getValues('artColors') || []
      const nextSelectedColors = toggleSelectingItem(selectedColors, colorType.value)
      setValue('artColors', nextSelectedColors) // clear the value if selecting other, so the input is empty
      trigger('artColors')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') onNext()
    }

    // Attach the event listener when the component mounts
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderItem = (colorType: ColorType) => {
    const { label, value, colorClasses } = colorType
    const isSelected = selectedColors.includes(value)

    return (
      <div key={value} className="flex flex-col gap-2" onClick={makeHandleToggleArtStyle(colorType)} role="button">
        <div
          className={cn(
            'relative box-border w-full overflow-hidden rounded-xl border-[3px] bg-white lg:rounded-2xl',
            isSelected ? 'border-primary' : 'border-transparent',
          )}
        >
          <div className="grid aspect-[3/1] h-[75px] w-full grid-cols-5 grid-rows-1 rounded-lg md:aspect-[4/3] md:h-[240px] lg:rounded-2xl">
            {colorClasses.map((colorClass, index) => (
              <div key={index} className={cn('h-full w-full', colorClass, value === 'White' && '')} />
            ))}
          </div>
        </div>
        <div
          className={cn(
            'pb-2 text-center text-[15px] font-medium leading-[1.25] lg:text-[22px]',
            isSelected ? 'text-primary' : 'text-black',
          )}
        >
          {label}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 px-4 text-kokushoku-black lg:gap-12 lg:px-[12px]">
      {/* preview */}
      <PreviewText displayProps={['roomType', 'artStyles']} />
      <div className="text-center text-[25px] font-semibold leading-[1.25] lg:text-[36px]">
        What colors do you prefer?
      </div>
      <div className="container h-full w-full overflow-y-auto px-4 lg:px-0">
        <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-3 md:gap-3 lg:grid-cols-4 lg:gap-4 xl:grid-cols-5">
          {COLOR_TYPES.map(renderItem)}
        </div>
      </div>
    </div>
  )
}

export default ColorStep
